import React from 'react'
import EtContentEditorLight2 from "@/utils/EtContentEditorLight2"
import { useCreateNewUserReflectionMutation } from "@/features/userReflections/userReflectionsApiSlice"
import EtWabReflectionIcon1SVGComp from "@/assets/img/icons/svgComp/EtWabReflectionIcon1SVGComp"

const EtUpdateUserReflectionComp = ({
  atomicRecord,
  onClose,
  refetchUserReflections,
  refetchUserReflectionsAll,
  defaultTrailId,
  defaultSessionId,
  allUsers=[],      // 06 Feb 2026
  canSyncGoogleDoc = false, // 31 Aug 2026 
}) => {

  // First Created: 22 Sep 2024  
  // Author: Atul Pandey
  // Updated - 04 Dec 2025 - Built the complete logic to update a user reflection note inside a UserReflection record and cascade to UserReflectionAll 
  // Updated - 06 Feb 2026 - added allUsers prop to support sharedWith feature 
  // Updated - 31 Aug 2026 - added canSyncGoogleDoc

  // console.log('allUsers in EtUpdateUserReflectionComp :', allUsers)

  // -------------------------------------------------------------
  // STEP 1 — Validate and Extract Essentials from atomicRecord
  // -------------------------------------------------------------

  // 1️⃣ Weekly Note ID (subdocument inside userReflectionNotes array)
  const weeklyNoteId = atomicRecord?.parentNoteId;
  

  // 2️⃣ Extract core text fields
  const initialText = atomicRecord?.reflectionNoteText || "";

  // 3️⃣ Extract Magic Moment status
  const initialMagicMomentId = atomicRecord?.userMagicMomentId || null;

  // 4️⃣ For building the update payload
  const userId = atomicRecord?.userId;
  const userOrgId = atomicRecord?.userOrgId;

  // 5️⃣ Week boundaries — DO NOT CHANGE
  const weekStarting = atomicRecord?.parentReflectionId?.weekStarting;


  // ------------------------------------------------------
  // STEP 2 — Component State
  // ------------------------------------------------------

  const [editorValue, setEditorValue] = React.useState(initialText);
  const [markAsMagicMoment, setMarkAsMagicMoment] = React.useState(
    !!initialMagicMomentId
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const [editorKey, setEditorKey] = React.useState(
    "reflection-editor-" + Date.now().toString()
  );

  // ⭐ 06 Feb 2026 — SharedWith state (reflection-level)
  const [sharedWith, setSharedWith] = React.useState(
    atomicRecord?.sharedWith || []
  );

  // 31 Aug 2026 - Per-save Google Docs sync choice
  const [
    syncGoogleDoc,
    setSyncGoogleDoc,
  ] = React.useState(
    canSyncGoogleDoc
  );

  // If Google Docs becomes unavailable while modal is open,
  // make sure this save cannot request synchronization.
  React.useEffect(() => {

    if (
      !canSyncGoogleDoc
    ) {

      setSyncGoogleDoc(
        false
      );

    }

  }, [
    canSyncGoogleDoc,
  ]);

  // ------------------------------------------------------
  // STEP 3 — RTK mutation
  // ------------------------------------------------------

  const [createNewUserReflection] = useCreateNewUserReflectionMutation();

  // ⭐ 06 Feb 2026 - Shared With (Reflection-level)
  const sharedWithIds = React.useMemo(() => {
    return (atomicRecord?.sharedWith || []).map(sw =>
      sw.userId?._id || sw.userId
    );
  }, [atomicRecord]);

  const shareableUsers = React.useMemo(() => {
    return allUsers.filter(u =>
      u._id !== atomicRecord.userId?._id   // ❌ cannot share with self
    );
  }, [allUsers, atomicRecord]);


  // ------------------------------------------------------
  // STEP 4 — Base Update Payload 
  // ------------------------------------------------------
  const baseUpdatePayload = {
    userId,
    userOrgId,
    weekStarting,               // ❗ DO NOT modify week boundaries
    userReflectionNotes: [
      {
        _id: weeklyNoteId,      // we will PATCH exactly this note
        userReflectionNoteText: editorValue,
        userReflectionNoteUpdateDate: new Date(),
        markAsMagicMoment: markAsMagicMoment,
        userMagicMomentId: atomicRecord?.userMagicMomentId || null,
      }
    ]
  };

  // ------------------------------------------------------
  // STEP 5 — Handle Editor Change
  // ------------------------------------------------------
  const handleEditorChange = (value) => {
    setEditorValue(value);
  };

  // ⭐ 06 Feb 2026 - Handle Add SharedWith user
  const handleAddSharedUser = (userId) => {
    if (!userId) return;

    setSharedWith(prev => {
      const exists = prev.some(
        sw => (sw.userId?._id || sw.userId) === userId
      );
      if (exists) return prev;

      return [...prev, { userId }];
    });
  };

  // ⭐ 06 Feb 2026 - Handle Remove SharedWith user
  const handleRemoveSharedUser = (userId) => {
    setSharedWith(prev =>
      prev.filter(
        sw => (sw.userId?._id || sw.userId) !== userId
      )
    );
  };

  // ⭐ 06 Feb 2026 - Add ALL users at once
  const handleAddAllSharedUsers = () => {
    const allShareableIds = shareableUsers.map(u => u._id);

    setSharedWith(prev => {
      const existingIds = prev.map(
        sw => sw.userId?._id || sw.userId
      );

      const newEntries = allShareableIds
        .filter(id => !existingIds.includes(id))
        .map(id => ({ userId: id }));

      return [...prev, ...newEntries];
    });
  };

  // ⭐ 06 Feb 2026 - Remove ALL users at once
  const handleRemoveAllSharedUsers = () => {
    setSharedWith([]);
  };



  // ------------------------------------------------------
  // STEP 6 — Save Handler (Update Weekly + Cascade to Atomic)
  // ------------------------------------------------------
  const handleSave = async () => {
    if (!editorValue || editorValue.trim() === "") {
      alert("Reflection cannot be empty.");
      return;
    }

    try {
      setIsSaving(true);

      // Build final payload (extends Step 4 base payload)
      const payload = {
        ...baseUpdatePayload,
        userReflectionNotes: [
          {
            _id: weeklyNoteId,
            userReflectionNoteText: editorValue,
            userReflectionNoteUpdateDate: new Date(),
            markAsMagicMoment,
          },
        ],
        sharedWith: sharedWith, // ⭐ 06 Feb 2026 — reflection-level sharing

        // 31 Aug 2026
        // Transient instruction only - not persisted in UserReflectionAll
        syncGoogleDoc:
          canSyncGoogleDoc &&
          syncGoogleDoc,
      };

      // 🔁 Send to backend (same endpoint as weekly WAB reflections)
      await createNewUserReflection(payload).unwrap();

      // 🔄 Refresh UI
      refetchUserReflectionsAll?.();
      refetchUserReflections?.();

      // Close modal
      onClose();
    } catch (err) {
      console.error("Failed to update reflection:", err);
      alert("Something went wrong while updating the reflection.");
    } finally {
      setIsSaving(false);
    }
  };

  // ------------------------------------------------------
  // STEP 7 — No Atomic Record
  // ------------------------------------------------------

  if (!atomicRecord) return <div>No Reflection Loaded</div>;


  return (
    <div className="p-4 bg-white rounded">

      <div className="flex justify-start items-center py-4">
        <EtWabReflectionIcon1SVGComp className="h-6 w-6 md:h-8 md:w-8 text-orange-600" />
        <p className="text-xl font-bold text-gray-800 mx-1 px-1">
          Update Your Reflection
        </p>
      </div>

      {/* --------------------------- */}
      {/* Reflection Editor Section   */}
      {/* --------------------------- */}
      <EtContentEditorLight2
        value={editorValue}
        handleChange={handleEditorChange}
        placeholderText="Update your reflection..."
        editorKey={editorKey}
      />

      {/* Magic Moment toggle */}
      <div className="flex items-center my-3">
        <input
          type="checkbox"
          className="mr-2"
          checked={markAsMagicMoment}
          disabled={!!atomicRecord.userMagicMomentId}  // cannot unmark
          onChange={(e) => setMarkAsMagicMoment(e.target.checked)}
        />
        <label className="text-xs text-gray-600">
          Mark as a Magic Moment
        </label>
      </div>


      {/* ------------------------------------------- */}
      {/* 31 Aug 2026 - Google Docs Sync              */}
      {/* ------------------------------------------- */}

      {canSyncGoogleDoc && (

        <div
          className="
            flex
            items-center
            my-3
            px-3
            py-2
            bg-gray-50
            border
            border-gray-200
            rounded
          "
        >

          <input
            type="checkbox"
            checked={syncGoogleDoc}
            onChange={
              e =>
                setSyncGoogleDoc(
                  e.target.checked
                )
            }
            className="mr-2"
          />

          <div>

            <label className="text-xs font-semibold text-gray-700">
              Sync this reflection to Google Docs
            </label>

            <p className="text-[10px] text-gray-500">
              Save this reflection to your connected Google Docs.
            </p>

          </div>

        </div>

      )}

      {/* --------------------------------- */}
      {/* 06 Feb 2026 - Shared With Section */}
      {/* --------------------------------- */}
      <div className="mt-4">

          <div className='flex justify-start items-center flex-wrap'>
              <p className="text-xs font-semibold text-gray-700 m-1">
                Share this reflection with
              </p>

              {/* Add user dropdown */}
              <select
                className="border rounded px-2 py-1 text-xs"
                defaultValue=""
                onChange={(e) => {
                  handleAddSharedUser(e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="" disabled>
                  Add user from your org
                </option>
                {shareableUsers
                  .filter(u =>
                    !sharedWith.some(
                      sw => (sw.userId?._id || sw.userId) === u._id
                    )
                  )
                  .map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name || user.username}
                    </option>
                  ))}
              </select>

              {/* Bulk actions */}
              <div className="flex items-center mx-2 space-x-2">
                {/* Select All — show only if NOT all users are selected */}
                {sharedWith.length < shareableUsers.length && (
                  <button
                    type="button"
                    onClick={handleAddAllSharedUsers}
                    className="text-xs text-white bg-lime-500 px-2 py-1 hover:underline rounded"
                  >
                    Select All
                  </button>
                )}

                {/* Remove All — show only if at least one user is selected */}
                {sharedWith.length > 0 && (
                  <button
                    type="button"
                    onClick={handleRemoveAllSharedUsers}
                    className="text-xs text-white bg-red-500 px-2 py-1 hover:underline rounded"
                    disabled={sharedWith.length === 0}
                  >
                    Remove All
                  </button>
                )}
              </div>

          </div>

          {/* Selected users */}
          <div className="flex flex-wrap gap-2 my-2 py-2">
            {sharedWith.map(sw => {
              const uid = sw.userId?._id || sw.userId;
              const user = allUsers.find(u => u._id === uid);

              if (!user) return null;

              return (
                <span
                  key={uid}
                  className="flex items-center bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs"
                >
                  {user.name || user.username}
                  <button
                    onClick={() => handleRemoveSharedUser(uid)}
                    className="ml-1 text-orange-600 hover:text-orange-800"
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
      </div>


      {/* Save / Cancel Buttons */}
      <div className="flex space-x-3 mt-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-lime-600 text-white rounded-full text-sm"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>

        <button
          onClick={onClose}
          className="px-4 py-2 bg-orange-600 text-white rounded-full text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );

}

export default EtUpdateUserReflectionComp